<template>
    <section class="seo-section airport-page-section">
        <div class="seo-section__container">
            <h2 class="seo-section__title airport-page-title">{{ blok.title }}</h2>
            <div
                class="seo-section__content"
                :style="{ '--main-bg-color': `${seoContentHeight}px` }"
                :class="{
                    'is-opened': isOpened,
                    'is-with-toggle': withShowMoreBtn,
                }"
            >
                <div ref="seoContent" class="rich-text-wrapper">
                    <RichText :richtext="blok.text" />
                </div>
            </div>
            <div v-if="withShowMoreBtn" class="seo-section__btn">
                <ShowMoreBtn
                    :is-opened="isOpened"
                    :show-text="t('read_more')"
                    :hide-text="t('hide_more')"
                    @click="isOpened = !isOpened"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { L4SeoSectionConfig } from '~/types';

const { t } = useI18n();

defineProps<{ blok: L4SeoSectionConfig }>();

const isOpened = ref(false);
const withShowMoreBtn = ref(false);
const seoContent = ref<HTMLDivElement | null>(null);
const MAX_HEIGHT = 144;
const offSet = 20;
const seoContentHeight = ref<number>(0);
let windowWidth;

function showMoreBtnVisibility() {
    // Prevent wrong resize event handling on mobile scroll
    if (windowWidth === window.innerWidth) return;

    const seoContentElement = seoContent?.value;
    if (seoContentElement) {
        const savedOpenedState = isOpened.value;
        // For correct full height checking
        isOpened.value = true;
        seoContentElement.style.transitionDuration = '0s';
        nextTick(() => {
            seoContentHeight.value = seoContentElement.getBoundingClientRect().height + offSet;
            withShowMoreBtn.value = seoContentHeight.value > MAX_HEIGHT;
            // Restore state
            seoContentElement.style.transitionDuration = '0.2s';
            isOpened.value = savedOpenedState;
        });
    }
}

if (!import.meta.server) {
    onMounted(() => {
        showMoreBtnVisibility();
        windowWidth = window.innerWidth;
        window.addEventListener('resize', showMoreBtnVisibility);
    });
    onUnmounted(() => window.removeEventListener('resize', showMoreBtnVisibility));
}
</script>

<style lang="postcss" scoped>
.seo-section {
    &__container {
        @apply md:container;
    }

    &__content {
        @apply max-h-36 overflow-hidden relative mb-6 md:mb-0;
        transition: max-height 0.2s;

        &.is-with-toggle {
            &:before {
                content: '';
                @apply absolute left-0 bottom-0 h-20 w-full;
                background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
            }
        }

        &.is-opened {
            max-height: var(--main-bg-color);

            &:before {
                @apply hidden;
            }
        }

        :deep(h2),
        :deep(h3),
        :deep(h4),
        :deep(h5) {
            @apply font-bold mb-4;
        }

        :deep(h1) {
            @apply text-3xl;
        }

        :deep(h2) {
            @apply text-base mt-0 leading-6;
        }

        :deep(h3),
        :deep(h4) {
            @apply text-base leading-6 mb-4 mt-0;
        }

        :deep(.lead) {
            @apply text-lg md:text-xl leading-7 block mb-6 md:mb-8;
        }

        :deep(p) {
            &:not(:last-child) {
                @apply mb-4;
            }
        }

        :deep(a) {
            @apply text-[#0094FF] hover:underline hover:text-[#0084E3];
        }
    }

    &__btn {
        @apply flex justify-center md:justify-end;
    }
}
</style>
